import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { Search, Users, Ban, UserCheck } from 'lucide-react';
import { userService, UserDto, PagedResult } from '@/features/admin/services/userService';
import { toast } from 'sonner';

export const UserListPage = () => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<PagedResult<UserDto> | null>(null);

  useEffect(() => {
    fetchUsers();
  }, [searchTerm]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAllUsers({
        searchTerm: searchTerm || undefined,
        pageNumber: 1,
        pageSize: 50,
      });
      setUsers(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل تحميل المستخدمين');
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users?.items.filter((u) =>
    u.userId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-accent-dark mb-2">إدارة المستخدمين</h1>
          <p className="text-accent-dark/70">إدارة المستخدمين والأدوار والصلاحيات</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-2 border-accent-greenLight/20">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-accent-dark/50 w-5 h-5" />
              <Input
                placeholder="بحث في المستخدمين..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pr-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      {filteredUsers.length === 0 ? (
        <Card className="border-2 border-accent-greenLight/20">
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-accent-dark/30" />
            <p className="text-lg text-accent-dark/70 mb-2">لا يوجد مستخدمين</p>
            <p className="text-sm text-accent-dark/50">
              {searchTerm ? 'لم يتم العثور على مستخدمين تطابق البحث' : 'سيتم عرض المستخدمين هنا'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredUsers.map((user) => (
            <Card key={user.id} className="border-2 border-accent-greenLight/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-accent-dark">{user.displayName}</h3>
                      <span className={`px-2 py-1 text-xs rounded ${
                        user.isBanned 
                          ? 'bg-accent-red/10 text-accent-red' 
                          : 'bg-secondary/10 text-secondary'
                      }`}>
                        {user.role}
                      </span>
                      {user.isBanned && (
                        <span className="px-2 py-1 text-xs bg-accent-red/20 text-accent-red rounded">
                          محظور
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-accent-dark/70">{user.userId}</p>
                    {user.isBanned && user.banReason && (
                      <p className="text-xs text-accent-red mt-1">السبب: {user.banReason}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {user.isBanned ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            await userService.unbanUser(user.id);
                            toast.success('تم إلغاء الحظر بنجاح');
                            fetchUsers();
                          } catch (error: any) {
                            toast.error(error.response?.data?.message || 'فشل إلغاء الحظر');
                          }
                        }}
                      >
                        <UserCheck className="w-4 h-4 mr-1" />
                        إلغاء الحظر
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-accent-red border-accent-red/30 hover:bg-accent-red/10"
                        onClick={async () => {
                          const reason = prompt('سبب الحظر:');
                          if (reason) {
                            try {
                              await userService.banUser(user.id, reason);
                              toast.success('تم حظر المستخدم بنجاح');
                              fetchUsers();
                            } catch (error: any) {
                              toast.error(error.response?.data?.message || 'فشل حظر المستخدم');
                            }
                          }
                        }}
                      >
                        <Ban className="w-4 h-4 mr-1" />
                        حظر
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
